import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "../firebase/authContext";
import { d1GetUnreadCounts } from "../services/d1Client";
import SocialModal from "../components/Social/SocialModal";
import UserCardModal from "../components/Social/UserCardModal";

const SocialContext = createContext({
  openSocialModal: () => {},
  closeSocialModal: () => {},
  openChatWithUser: () => {},
  openUserCard: () => {},
  closeUserCard: () => {},
  refreshUnreadCounts: () => {},
  unreadCounts: { unreadMessages: 0, pendingRequests: 0, total: 0 },
});

export const useSocial = () => useContext(SocialContext);

export const SocialProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const currentUid = currentUser?.uid;

  // Social Modal State (Chat & Friends)
  const [socialModalOpen, setSocialModalOpen] = useState(false);
  const [socialInitialTab, setSocialInitialTab] = useState(0);
  const [chatTargetUser, setChatTargetUser] = useState(null);

  // User Card Modal State (Avatar Popover)
  const [userCardOpen, setUserCardOpen] = useState(false);
  const [targetUserCard, setTargetUserCard] = useState(null);

  // Unread badge counts
  const [unreadCounts, setUnreadCounts] = useState({
    unreadMessages: 0,
    pendingRequests: 0,
    total: 0,
  });

  // Fetch unread count from D1. Optional hint applies an immediate local decrement
  // so app-bar / tab badges update before the server round-trip returns.
  const refreshUnreadCounts = useCallback(async (hint) => {
    if (!currentUser) {
      setUnreadCounts({ unreadMessages: 0, pendingRequests: 0, total: 0 });
      return;
    }
    if (hint && typeof hint === "object" && Number(hint.clearedMessages) > 0) {
      setUnreadCounts((prev) => {
        const unreadMessages = Math.max(0, (prev.unreadMessages || 0) - Number(hint.clearedMessages));
        const pendingRequests = prev.pendingRequests || 0;
        return {
          unreadMessages,
          pendingRequests,
          total: unreadMessages + pendingRequests,
        };
      });
    }
    try {
      const counts = await d1GetUnreadCounts();
      const unreadMessages = counts?.unreadMessages || 0;
      const pendingRequests = counts?.pendingRequests || 0;
      setUnreadCounts({
        unreadMessages,
        pendingRequests,
        total: unreadMessages + pendingRequests,
      });
    } catch (e) {
      // ignore
    }
  }, [currentUser]);

  useEffect(() => {
    setSocialModalOpen(false);
    setChatTargetUser(null);
    setUserCardOpen(false);
    setTargetUserCard(null);
    setUnreadCounts({ unreadMessages: 0, pendingRequests: 0, total: 0 });
  }, [currentUid]);

  // Periodic polling for unread messages / pending requests (every 15s)
  useEffect(() => {
    if (currentUser && !currentUser.isGuest && currentUser.uid !== "local_guest_user") {
      refreshUnreadCounts();
      const timer = setInterval(refreshUnreadCounts, 15000);
      return () => clearInterval(timer);
    }
  }, [currentUser, refreshUnreadCounts]);

  // Open the main Social Modal
  const openSocialModal = useCallback((tab = 0, targetUser = null) => {
    setSocialInitialTab(tab);
    setChatTargetUser(targetUser || null);
    setSocialModalOpen(true);
  }, []);

  const closeSocialModal = useCallback(() => {
    setSocialModalOpen(false);
    setChatTargetUser(null);
    refreshUnreadCounts();
  }, [refreshUnreadCounts]);

  // Direct helper to initiate a chat with any user
  const openChatWithUser = useCallback((user, options = {}) => {
    const targetUid = user?.uid || user?.authorId || user?.userId;
    if (!targetUid) return;
    if (currentUser?.uid && targetUid === currentUser.uid) {
      setTargetUserCard({
        uid: targetUid,
        displayName: user.displayName || user.userDisplayName,
        photoURL: user.photoURL || user.authorPhotoURL,
      });
      setUserCardOpen(true);
      return;
    }
    setUserCardOpen(false);
    setSocialInitialTab(0);
    setChatTargetUser({
      ...user,
      uid: targetUid,
      verseQuote: options.verseQuote || null,
    });
    setSocialModalOpen(true);
  }, [currentUser]);

  // Open user profile popover card
  const openUserCard = useCallback((uidOrUser, initialData = {}) => {
    if (!uidOrUser) return;
    if (typeof uidOrUser === "string") {
      setTargetUserCard({
        uid: uidOrUser,
        ...initialData,
      });
    } else {
      setTargetUserCard({
        uid: uidOrUser.uid || uidOrUser.authorId || uidOrUser.userId,
        displayName: uidOrUser.displayName || uidOrUser.userDisplayName,
        photoURL: uidOrUser.photoURL || uidOrUser.authorPhotoURL,
        ...initialData,
      });
    }
    setUserCardOpen(true);
  }, []);

  const closeUserCard = useCallback(() => {
    setUserCardOpen(false);
  }, []);

  // Legacy window event used by older menu / community buttons
  useEffect(() => {
    const onOpenChat = (event) => {
      const detail = event?.detail || {};
      if (detail.uid) {
        openChatWithUser(
          {
            uid: detail.uid,
            displayName: detail.displayName,
            photoURL: detail.photoURL,
          },
          { verseQuote: detail.verseQuote || null }
        );
      } else {
        openSocialModal(0);
      }
    };
    window.addEventListener("open_chat_with_user", onOpenChat);
    return () => window.removeEventListener("open_chat_with_user", onOpenChat);
  }, [openChatWithUser, openSocialModal]);

  return (
    <SocialContext.Provider
      value={{
        openSocialModal,
        closeSocialModal,
        openChatWithUser,
        openUserCard,
        closeUserCard,
        refreshUnreadCounts,
        unreadCounts,
      }}
    >
      {children}

      {/* Ubiquitous User Profile Popover / Dialog */}
      <UserCardModal
        open={userCardOpen}
        onClose={closeUserCard}
        targetUser={targetUserCard}
        onStartChat={(u) => openChatWithUser(u)}
        onFriendshipChanged={refreshUnreadCounts}
      />

      {/* Main Messaging & Friends Dialog */}
      <SocialModal
        open={socialModalOpen}
        onClose={closeSocialModal}
        initialTab={socialInitialTab}
        initialTargetUser={chatTargetUser}
        onOpenUserCard={(u) => openUserCard(u)}
        onUnreadChange={refreshUnreadCounts}
      />
    </SocialContext.Provider>
  );
};
